import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LockSimple, LockSimpleOpen } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const money = (n) =>
  n == null ? "—" : "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
const pct = (n) => (n == null ? "—" : `${n}%`);
const YEARS = [new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2];

const Kpi = ({ label, value, sub, testid }) => (
  <div className="rounded-xl border border-zinc-800/60 bg-zinc-950/40 p-4" data-testid={testid}>
    <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{label}</div>
    <div className="mt-1 font-mono-data text-2xl font-bold tabular-nums text-white">{value}</div>
    {sub && <div className="mt-0.5 text-[11px] text-zinc-500">{sub}</div>}
  </div>
);

const BAR = [
  ["material", "Material", "bg-sky-500"],
  ["labor", "Labor", "bg-violet-500"],
  ["other", "Other", "bg-zinc-500"],
  ["overhead", "Overhead", "bg-amber-500"],
  ["profit", "Profit", "bg-emerald-500"],
];

export const BenchmarkReport = () => {
  const [year, setYear] = useState(YEARS[0]);
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ overhead: "", employees: "", assets: "", liabilities: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/reports/benchmark?year=${year}`);
    setD(data);
    const a = data.assumptions || {};
    setForm({
      overhead: a.overhead || "",
      employees: a.employees || "",
      assets: a.assets || "",
      liabilities: a.liabilities || "",
    });
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  const locked = d?.assumptions?.locked;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/reports/benchmark/assumptions?year=${year}`, {
        overhead: Number(form.overhead) || 0,
        employees: Number(form.employees) || 0,
        assets: Number(form.assets) || 0,
        liabilities: Number(form.liabilities) || 0,
      });
      toast.success("Assumptions saved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
    setSaving(false);
  };

  const toggleLock = async () => {
    try {
      await api.put(`/reports/benchmark/assumptions?year=${year}`, { locked: !locked });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change lock");
    }
  };

  if (!d) return <div className="text-sm text-zinc-500">Loading…</div>;
  const w = d.where_100_goes || {};

  return (
    <div className="space-y-6" data-testid="benchmark-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-lg border border-zinc-800 p-1">
          {YEARS.map((y) => (
            <button
              key={y}
              type="button"
              data-testid={`benchmark-year-${y}`}
              onClick={() => setYear(y)}
              className={`rounded-md px-3 py-1.5 text-xs font-bold tabular-nums transition-colors duration-150 ${
                y === year ? "bg-indigo-500 text-white" : "text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              {y}
            </button>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          data-testid="benchmark-lock"
          onClick={toggleLock}
          className={`h-9 text-xs uppercase tracking-widest ${
            locked
              ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
              : "border-zinc-700 text-zinc-300"
          }`}
        >
          {locked ? <LockSimple size={14} weight="bold" className="mr-2" /> : (
            <LockSimpleOpen size={14} weight="bold" className="mr-2" />
          )}
          {locked ? `${year} locked` : `Lock ${year}`}
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi testid="benchmark-revenue" label="Gross revenue" value={money(d.revenue)} sub={`${money(d.collected)} collected`} />
        <Kpi testid="benchmark-gross" label="Gross profit" value={money(d.gross_profit)} sub={`${pct(d.gross_margin_pct)} margin · COGS ${money(d.cogs)}`} />
        <Kpi testid="benchmark-ebitda" label="EBITDA" value={money(d.ebitda)} sub={`${pct(d.ebitda_margin_pct)} margin`} />
        <Kpi testid="benchmark-dso" label="Days sales outstanding" value={d.dso_days == null ? "—" : `${d.dso_days} d`} sub={`${money(d.ar_outstanding)} outstanding`} />
        <Kpi testid="benchmark-per-employee" label="Revenue / employee" value={money(d.revenue_per_employee)} sub={`${d.employees || 0} employees`} />
        <Kpi testid="benchmark-per-sqft" label="Revenue / sq ft" value={d.revenue_per_sqft == null ? "—" : `$${d.revenue_per_sqft}`} sub={`${d.sqft} ft² installed`} />
        <Kpi testid="benchmark-equity" label="Owner equity" value={money(d.equity)} sub={`${money(d.assets)} assets − ${money(d.liabilities)} liab.`} />
        <Kpi testid="benchmark-labor" label="Direct cost split" value={money(d.material + d.labor)} sub={`Material ${money(d.material)} · Labor ${money(d.labor)}`} />
      </div>

      <div className="rounded-xl border border-zinc-800/60 bg-zinc-950/40 p-4">
        <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
          Where every $100 of revenue goes
        </div>
        <div className="flex h-4 w-full overflow-hidden rounded-full bg-zinc-800">
          {BAR.map(([k, , cls]) => (
            <div key={k} className={cls} style={{ width: `${Math.max(0, w[k] || 0)}%` }} title={`${k}: ${pct(w[k])}`} />
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-400">
          {BAR.map(([k, label, cls]) => (
            <span key={k} className="flex items-center gap-1.5">
              <span className={`h-2.5 w-2.5 rounded-full ${cls}`} /> {label} {pct(w[k])}
            </span>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800/60 bg-zinc-950/40 p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Assumptions ({year})
          </div>
          <span className="text-[11px] text-zinc-500">
            Overhead & headcount feed EBITDA and per‑employee; balance sheet feeds equity.
          </span>
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            ["overhead", "Annual overhead ($)"],
            ["employees", "Employees (#)"],
            ["assets", "Total assets ($)"],
            ["liabilities", "Total liabilities ($)"],
          ].map(([k, label]) => (
            <label key={k} className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{label}</span>
              <Input
                type="number"
                data-testid={`benchmark-input-${k}`}
                disabled={locked}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="mt-1 h-10 border-zinc-800 bg-zinc-950 disabled:opacity-40"
              />
            </label>
          ))}
        </div>
        <Button
          type="button"
          data-testid="benchmark-save"
          onClick={save}
          disabled={saving || locked}
          className="mt-3 h-10 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400 disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save assumptions"}
        </Button>
      </div>
    </div>
  );
};
